import { useCallback, useEffect, useRef, useState } from "react"

import { Helmet } from "react-helmet"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Button, Card, Container, Divider, Grid, Input } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { useThrottle } from "simplydo/react/hooks"
import uuid from "uuid"

import api from "api"
import NewChallengeModal from "components/challenges/NewChallengeModal"
import ChallengeCard from "components/lib/Cards/ChallengeCard"
import { OrgSwitcherDropdown } from "components/lib/OrgSwitcherTray"
import { EmptyBox, PlaceholderItem, PlaceholderStack } from "components/lib/UI"
import DiscoverImage from "src/images/discover.png"
import { useAppSelector } from "store"
import useTheme from "theme/useTheme"
import util from "utils/utils"

const Challenges = () => {
  const { t } = useTranslation()
  const user = useAppSelector((state) => state.user)

  const [isLoading, setIsLoading] = useState(true)
  const currentFetchId = useRef("")
  const [newChallengeOpen, setNewChallengeOpen] = useState(false)
  const [challengeData, setChallengeData] = useState<OpenAPI.GET<"/challenges/all">["response"]>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const theme = useTheme()

  const { challenges } = challengeData ?? {}

  const fetchChallenges = useThrottle(
    (query) => {
      const fetchId = uuid.v4()
      currentFetchId.current = fetchId

      api.challenges.getAll(
        {
          query,
        },
        (data) => {
          if (fetchId !== currentFetchId.current) return
          setChallengeData(data)
          setIsLoading(false)
        },
        (err) => {
          toast.error(err.message)
          setIsLoading(false)
        },
      )
    },
    400,
    [],
  )

  const getChallenges = useCallback(
    (query) => {
      setIsLoading(true)
      setSearchQuery(query)
      fetchChallenges(query)
    },
    [fetchChallenges],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: Should refetch challenges when user organisation changes
  useEffect(() => {
    getChallenges("")
  }, [getChallenges, user?.ownerOrganisation?._id])

  return (
    <div>
      {newChallengeOpen ? (
        <NewChallengeModal newChallengeOpen={newChallengeOpen} closeModal={() => setNewChallengeOpen(false)} />
      ) : null}
      <Helmet title={t("common:capitalise", { key: "generic.challenges" })} />
      <div style={{ marginTop: 20 }}>
        <Container width={16}>
          <Grid stackable>
            <Grid.Row columns={2}>
              <Grid.Column computer={11}>
                <h1 style={{ color: "#3b3b3b", margin: "0px" }}>
                  {user ? t("common:capitalise", { key: "generic.challenges" }) : t("challenges.yours.sidebar.all")}
                </h1>
                {user?.ownerOrganisations?.length > 1 ? (
                  <p style={{ fontSize: "0.9rem" }}>
                    in <span style={{ fontWeight: 600 }}>{user.ownerOrganisation.name}</span>
                    {" - "}
                    <OrgSwitcherDropdown
                      pointing="top left"
                      basic
                      trigger={
                        <a tabIndex={0} role="button" onClick={(e) => e.preventDefault()}>
                          Change organisation
                        </a>
                      }
                    />
                  </p>
                ) : null}
              </Grid.Column>
              <Grid.Column computer={5} textAlign="right" verticalAlign="bottom">
                {util.canCreateChallenges(user) && (
                  <Button
                    size={!theme.sizes.isComputer ? "tiny" : "medium"}
                    icon="plus"
                    content={
                      !theme.sizes.isComputer
                        ? `Create ${t("generic.challengeWithArticle")}`
                        : t("challenges.new.title")
                    }
                    onClick={() => setNewChallengeOpen(true)}
                  />
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Container>
      </div>

      <Container style={{ marginTop: "1rem" }}>
        <Grid stackable>
          <Grid.Row columns={2}>
            <Grid.Column width={16}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 24 }}>
                <Input
                  placeholder={t("challenges.all.search")}
                  loading={isLoading}
                  icon="search"
                  onChange={(_e, { value }) => getChallenges(value)}
                  value={searchQuery}
                  style={{ width: "33%" }}
                />
                {!challenges?.length && !isLoading ? (
                  searchQuery ? (
                    <EmptyBox style={{ width: "100%" }}>
                      <img
                        src={DiscoverImage}
                        style={{
                          maxHeight: 150,
                          maxWidth: "70%",
                          display: "block",
                          margin: "10px auto",
                        }}
                        alt="Discover"
                      />
                      <h2>{t("ideas.yours.sidebar.noResults")}</h2>
                    </EmptyBox>
                  ) : (
                    <>
                      <EmptyBox style={{ width: "100%" }}>
                        <img
                          src={DiscoverImage}
                          style={{
                            maxHeight: 150,
                            maxWidth: "70%",
                            display: "block",
                            margin: "10px auto",
                          }}
                          alt="Discover"
                        />

                        <h2>{t("challenges.all.noneOpen")}</h2>
                      </EmptyBox>
                      <Divider hidden />
                    </>
                  )
                ) : (
                  <>
                    <div style={{ width: "100%" }}>
                      <Card.Group itemsPerRow={4} stackable>
                        {!challenges?.length && isLoading
                          ? Array.from({ length: 8 }).map((_, i) => (
                              <Card key={i}>
                                <PlaceholderStack gap={5}>
                                  <PlaceholderItem height={200} />
                                </PlaceholderStack>
                              </Card>
                            ))
                          : challenges?.map((challenge) => (
                              <ChallengeCard
                                showIdeas
                                challenge={challenge}
                                ideas={challenge.ideas}
                                key={challenge._id}
                              />
                            ))}
                      </Card.Group>
                    </div>
                    <Divider />
                  </>
                )}
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider hidden />
      </Container>
    </div>
  )
}

export default Challenges
